import React, { useState, useEffect } from 'react';
import { clientAPI, ticketsAPI } from '../services/api';

interface DashboardProps {
  onNavigate: (view: string) => void;
  userName: string;
}

const Dashboard: React.FC<DashboardProps> = ({ onNavigate, userName }) => {
  const [stats, setStats] = useState({ totalClients: 0, openTickets: 0, totalDevices: 0, activeUsers: 0 });

  useEffect(() => { fetchStats(); }, []);

  const fetchStats = async () => {
    try {
      const [clientRes, ticketRes] = await Promise.all([clientAPI.getAll(), ticketsAPI.getAll({ status: 'open' })]);
      const clients = clientRes.data;
      setStats({
        totalClients: clients.length,
        openTickets: ticketRes.data?.length || 0,
        totalDevices: clients.reduce((s: number, c: any) => s + (c.devices?.length || 0), 0),
        activeUsers: clients.reduce((s: number, c: any) => s + (c.user_count || 0), 0)
      });
    } catch (error) { console.error('Error fetching stats:', error); }
  };

  const modules = [
    { id: 'list', name: 'Client Management', icon: '👤', desc: 'Manage clients and accounts', stat: `${stats.totalClients} Clients`, color: '#2563eb' },
    { id: 'device-management', name: 'Device Management', icon: '📱', desc: 'View all devices in one place', stat: `${stats.totalDevices} Devices`, color: '#7c3aed' },
    { id: 'camel', name: 'Platforms', icon: '🚀', desc: 'Camel • Starlink • Shadow', stat: 'Live Data', color: '#0891b2' },
    { id: 'alerts', name: 'Alerts', icon: '🚨', desc: 'Vehicles that stopped reporting', stat: 'Live', color: '#dc2626' },
    { id: 'tickets', name: 'Support Tickets', icon: '🎫', desc: 'View and manage tickets', stat: `${stats.openTickets} Open`, color: '#ea580c' },
    { id: 'support-hours', name: 'Support Hours', icon: '⏱️', desc: 'Track client support allocations', stat: 'View Usage', color: '#2563eb' },
    { id: 'data-management', name: 'Data Management', icon: '📡', desc: 'Monitor cellular data usage', stat: 'View Usage', color: '#0d9488' },
    { id: 'reports', name: 'Reports & Analytics', icon: '📊', desc: 'View insights and reports', stat: 'View Reports', color: '#059669' },
    { id: 'users', name: 'Staff Management', icon: '👥', desc: 'Manage system users', stat: `${stats.activeUsers} Users`, color: '#7c3aed' },
    { id: 'history', name: 'Activity History', icon: '📜', desc: 'View system activity logs', stat: 'View Logs', color: '#6b7280' },
    { id: 'install-check', name: 'Install Check', icon: '🔧', desc: 'Run vehicle installation checks', stat: 'Live Check', color: '#f59e0b' },
  ];

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '26px', fontWeight: 700, margin: 0, background: 'linear-gradient(135deg, #2563eb, #7c3aed)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
          👋 Welcome back, {userName}
        </h1>
        <p style={{ color: '#888', margin: '4px 0 0 0', fontSize: '14px' }}>Optitrac Device Management Platform</p>
      </div>

      {/* KPI bar */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '24px' }}>
        {[
          { label: 'Clients', value: stats.totalClients, icon: '👤', color: '#2563eb' },
          { label: 'Devices', value: stats.totalDevices, icon: '📱', color: '#7c3aed' },
          { label: 'Open Tickets', value: stats.openTickets, icon: '🎫', color: '#ea580c', pulse: stats.openTickets > 0 },
          { label: 'Users', value: stats.activeUsers, icon: '👥', color: '#059669' },
        ].map((t, i) => (
          <div key={i} style={{ background: '#fff', borderRadius: '12px', padding: '16px', textAlign: 'center', border: `2px solid ${t.color}20`, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <div style={{ fontSize: '22px', marginBottom: '4px' }}>{t.icon}</div>
            <div style={{ fontSize: '28px', fontWeight: 700, color: t.color }}>{t.value}</div>
            <div style={{ fontSize: '11px', color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t.label}</div>
          </div>
        ))}
      </div>

      {/* Module grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '14px' }}>
        {modules.map(m => (
          <div
            key={m.id}
            onClick={() => onNavigate(m.id)}
            style={{
              background: '#fff', borderRadius: '12px', padding: '20px', cursor: 'pointer',
              border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
              transition: 'box-shadow 0.2s, border-color 0.2s', position: 'relative', overflow: 'hidden'
            }}
            onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.1)'; e.currentTarget.style.borderColor = m.color; }}
            onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)'; e.currentTarget.style.borderColor = '#e5e7eb'; }}
          >
            <div style={{ fontSize: '32px', marginBottom: '10px' }}>{m.icon}</div>
            <div style={{ fontWeight: 700, fontSize: '16px', color: '#1f2937', marginBottom: '4px' }}>{m.name}</div>
            <div style={{ fontSize: '13px', color: '#9ca3af', marginBottom: '12px', lineHeight: 1.4 }}>{m.desc}</div>
            <span style={{ display: 'inline-block', padding: '4px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: 600, background: `${m.color}15`, color: m.color }}>
              {m.stat}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Dashboard;
